// Object list views from /api/list-views (apps/server/src/listViews.mjs).
// The server decides who sees, saves, shares and deletes a view; each view
// arrives with the caller's rights, and this file only carries them.

import type { FilterRow, SharedViewItem, SortState, ViewVisibility } from '@olytic/foundation';

import type { ObjectKey } from '../navigation';
import { ObjectsApiError } from './objectsApi';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export interface StoredView {
  id: string;
  /** A stable key for links (?view=open); seeded views only. */
  apiName: string | null;
  label: string;
  builtIn: 'recent' | null;
  visibility: ViewVisibility;
  owner: { id: string; name: string } | null;
  sharedWith: string[];
  canEdit: boolean;
  canManage: boolean;
  whyNotEdit?: string;
  /** Null: the object's own order (Recently viewed: most recent first). */
  sort: SortState | null;
  filters: FilterRow[];
  logic: string;
  /** Null: the object's default columns. */
  columns: string[] | null;
}

export interface ListViews {
  views: StoredView[];
  /** The view the list opens on: the caller's star, else the object's default. */
  defaultId: string | null;
  starredId: string | null;
  canPublish: boolean;
  me: string;
  /** Names for the owners and people these views are shared with. */
  people: { id: string; name: string }[];
}

/** What Save and Save as send. */
export interface ViewContent {
  sort: SortState | null;
  filters: FilterRow[];
  logic: string;
  columns: string[];
}

export interface ViewSettings {
  label?: string;
  visibility?: ViewVisibility;
  sharedWith?: string[];
}

const LIST_VIEWS = '/api/list-views';

const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

async function read<T>(response: Response, fallback: string): Promise<T> {
  let payload: unknown;
  try {
    payload = response.status === 204 ? null : await response.json();
  } catch {
    payload = undefined;
  }
  if (!response.ok) {
    const message =
      typeof payload === 'object' && payload !== null && typeof (payload as { error?: unknown }).error === 'string'
        ? (payload as { error: string }).error
        : `${fallback} (${response.status}).`;
    throw new ObjectsApiError(response.status, message);
  }
  return payload as T;
}

export async function loadListViews(fetcher: Fetch, object: ObjectKey, signal?: AbortSignal): Promise<ListViews> {
  const response = await fetcher(`${LIST_VIEWS}?object=${encodeURIComponent(object)}`, signal ? { signal } : undefined);
  return read<ListViews>(response, 'The views could not be loaded');
}

export async function createListView(fetcher: Fetch, object: ObjectKey, label: string, content: ViewContent): Promise<StoredView> {
  const response = await fetcher(LIST_VIEWS, json('POST', { object, label, ...content }));
  return (await read<{ view: StoredView }>(response, 'Could not save the view')).view;
}

export async function updateListView(fetcher: Fetch, id: string, patch: Partial<ViewContent> & ViewSettings): Promise<StoredView> {
  const response = await fetcher(`${LIST_VIEWS}/${encodeURIComponent(id)}`, json('PATCH', patch));
  return (await read<{ view: StoredView }>(response, 'Could not save the view')).view;
}

export async function deleteListView(fetcher: Fetch, id: string): Promise<void> {
  await read<null>(await fetcher(`${LIST_VIEWS}/${encodeURIComponent(id)}`, { method: 'DELETE' }), 'Could not delete the view');
}

export async function starListView(fetcher: Fetch, object: ObjectKey, viewId: string): Promise<void> {
  await read<unknown>(await fetcher(`${LIST_VIEWS}/default`, json('PUT', { object, viewId })), 'Could not change your default view');
}

/** Internal people whose name or email contains `query`, to share a view with. */
export async function searchPeople(fetcher: Fetch, query: string): Promise<{ id: string; name: string }[]> {
  const response = await fetcher(`${LIST_VIEWS}/people?q=${encodeURIComponent(query)}`);
  return (await read<{ people: { id: string; name: string }[] }>(response, 'Could not find people')).people;
}

/** A stored view as the library's picker and sharing parts show it. */
export function toSharedView(view: StoredView): SharedViewItem {
  return {
    id: view.id,
    label: view.label,
    builtIn: view.builtIn !== null,
    visibility: view.visibility,
    owner: view.owner,
    sharedWith: view.sharedWith,
    canEdit: view.canEdit,
    canManage: view.canManage,
  };
}

/** The view a link names (?view=open): by its key, or its id. */
export function findLinkedView(views: StoredView[], key: string | null | undefined): StoredView | undefined {
  if (!key) return undefined;
  return views.find((v) => v.apiName === key) ?? views.find((v) => v.id === key);
}
